import React, { useState, forwardRef, useRef, useEffect } from "react";
import clsx from "clsx";
import { useStore } from "react-redux";

import { makeStyles, createStyles, alpha } from "@material-ui/core/styles";

import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import Fade from "@material-ui/core/Fade";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";

import { type Theme } from "src/theme";
import { formatDollars } from "src/utils";
import InfoTooltip from "src/components/InfoTooltip";
import { useRecordEvent } from "src/hooks/useRecordEvent";

import StatementDrill, { StatementDrillType } from "./StatementDrill";
import ReportExplanationText from "./ReportExplanationText";
import type { ProfitLossReport as ProfitLossReportType } from "./report";

import Title, { DownloadButton } from "./Title";
import CompanyInfo from "./CompanyInfo";
import { printReport } from "./pdf";

const ComparisonTitle = "Profit/Loss Statement Comparison";
const StatementDrillDownTooltipText = "Click to view details";
const StatementDrillDownCompareTooltipText = "Click in number to view details";

type StatementDrillTypeWithReport =
  | (StatementDrillType & { report?: number })
  | null;

function getSortedKeys(objs: Record<string, any>[]): string[] {
  const keys = [
    ...new Set(
      objs.reduce<string[]>((ks, o) => [...ks, ...Object.keys(o)], [])
    ),
  ];
  keys.sort((a, b) => a.localeCompare(b));

  return keys;
}

const useHeaderStyles = makeStyles((theme) => ({
  reportType: {
    marginRight: theme.spacing(2),
    "& button": {
      lineHeight: 1,
    },
  },
}));

type ProfitLossType = "summary" | "detail";

interface ProfitLossReportHeaderProps {
  profitLossType: ProfitLossType;
  onProfitLossTypeChange: (t: ProfitLossType) => void;
  showEstimatedIncomeTax: boolean;
  onShowEstimatedIncomeTaxChange: (t: boolean) => void;
  onDownloadClick?: () => void;
  report: ProfitLossReportType;
}

function ProfitLossReportHeader({
  report,
  profitLossType,
  onProfitLossTypeChange,
  showEstimatedIncomeTax,
  onShowEstimatedIncomeTaxChange,
  onDownloadClick,
}: ProfitLossReportHeaderProps) {
  const recordEvent = useRecordEvent();

  const classes = useHeaderStyles();
  const isCompare = report.data.length > 1;

  return (
    <Title
      actions={
        <>
          <Fade in={true} timeout={600} style={{ transitionDelay: "800ms" }}>
            <ToggleButtonGroup
              className={classes.reportType}
              value={profitLossType}
              exclusive
              onChange={(e, value) => {
                if (value) {
                  const newValue = value === "summary" ? "summary" : "detail";
                  recordEvent({
                    type: "feature-adoption",
                    feature: "pl-feature",
                    element: "toggle-detail-summary",
                    value: newValue,
                  });
                  onProfitLossTypeChange(newValue);
                }
              }}
            >
              <ToggleButton value="detail">Detail</ToggleButton>
              <ToggleButton value="summary">Summary</ToggleButton>
            </ToggleButtonGroup>
          </Fade>
          <Fade in={true} timeout={600} style={{ transitionDelay: "900ms" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showEstimatedIncomeTax}
                  onChange={(e) => {
                    onShowEstimatedIncomeTaxChange(e.target.checked);
                    recordEvent({
                      type: "feature-adoption",
                      feature: "pl-feature",
                      element: "toggle-est-tax",
                    });
                  }}
                />
              }
              label="Toggle Est Tax"
            />
          </Fade>
          {onDownloadClick && (
            <DownloadButton onClick={onDownloadClick as any} />
          )}
        </>
      }
    >
      {isCompare ? ComparisonTitle : report.data[0].reportDetails.titleFull}
      <InfoTooltip text={<ReportExplanationText />} />
    </Title>
  );
}

const useBodyStyles = makeStyles((theme: Theme) =>
  createStyles({
    table: {
      margin: theme.spacing(3, "auto", 4),
      maxWidth: 480,
      width: "100%",
      borderSpacing: 0,
      borderCollapse: "collapse",
      "& td:first-child": {
        borderTopLeftRadius: theme.shape.borderRadius,
        borderBottomLeftRadius: theme.shape.borderRadius,
        paddingRight: theme.spacing(2),
      },
      "& td:last-child": {
        borderTopRightRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
      },
      "& td": {
        borderRadius: ({ isCompare }: { isCompare: boolean }) =>
          isCompare ? theme.shape.borderRadius : 0,
      },
    },
    left: {
      textAlign: "left",
    },
    right: {
      textAlign: "right",
    },
    bold: {
      fontWeight: theme.typography.fontWeightBold as number,
    },
    detail: {
      paddingLeft: theme.spacing(2),
    },
    divider: {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    blue: {
      color: theme.palette.primary.dark,
    },
    nowrap: {
      whiteSpace: "nowrap",
    },
    space: {
      padding: theme.spacing(0, 2),
    },
    pointer: {
      cursor: "pointer",
      "&:hover": {
        background: theme.palette.action.hover,
      },
    },
    highlightedRow: {
      "& td": {
        background: alpha(theme.palette.primary.main, 0.15),
      },
      "&:hover td": {
        background: alpha(theme.palette.primary.main, 0.25),
      },
    },
    highlightedCell: {
      background: alpha(theme.palette.primary.main, 0.15),
      "&:hover": {
        background: alpha(theme.palette.primary.main, 0.25),
      },
    },
    title: {
      paddingBottom: theme.spacing(2),
    },
  })
);

interface ProfitLossReportBodyProps {
  report: ProfitLossReportType;
  profitLossType: ProfitLossType;
  onStatementDrillChange: (st: StatementDrillTypeWithReport) => void;
  statementDrill: StatementDrillTypeWithReport;
  showEstimatedIncomeTax: boolean;
}

const ProfitLossReportBody = forwardRef(
  (
    {
      report,
      profitLossType,
      onStatementDrillChange,
      statementDrill,
      showEstimatedIncomeTax,
    }: ProfitLossReportBodyProps,
    ref
  ) => {
    const recordEvent = useRecordEvent();
    const isCompare = report.data.length > 1;
    const classes = useBodyStyles({ isCompare });
    const showDetail = profitLossType !== "summary";

    return (
      <Box className={classes.table} {...{ ref }} component="table">
        <thead>
          {isCompare && (
            <tr>
              <th />
              {report.data.map((data, i) => (
                <th
                  className={clsx(classes.nowrap, classes.space, classes.title)}
                  key={i}
                >
                  {data.reportDetails.dateRange}
                </th>
              ))}
            </tr>
          )}
          <tr>
            <th className={clsx(classes.bold, classes.left)}>Income</th>
            {report.data.map((data, i) => (
              <th className={clsx(classes.bold, classes.right)} key={i}>
                {formatDollars(data.totalSales)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {showDetail &&
            getSortedKeys(report.data.map((d) => d.salesByDepartment)).map(
              (department) => {
                const selectedRow =
                  statementDrill?.type === "sales" &&
                  statementDrill.platform === department;
                return (
                  <Tooltip
                    key={department}
                    title={
                      isCompare
                        ? StatementDrillDownCompareTooltipText
                        : StatementDrillDownTooltipText
                    }
                    placement="top"
                  >
                    <tr
                      className={clsx(
                        !isCompare && classes.pointer,
                        !isCompare && selectedRow && classes.highlightedRow
                      )}
                      onClick={(e) => {
                        recordEvent({
                          type: "feature-adoption",
                          feature: "pl-feature",
                          element: "click-to-view-details",
                        });
                        const report = parseInt(
                          (e.target as HTMLDivElement).getAttribute(
                            "data-index"
                          ) || "",
                          10
                        );

                        onStatementDrillChange(
                          selectedRow &&
                            (!isCompare || report === statementDrill?.report)
                            ? null
                            : {
                                type: "sales",
                                platform: department,
                                ...(isNaN(report) ? {} : { report }),
                              }
                        );
                      }}
                    >
                      <td className={classes.detail}>{department}</td>
                      {report.data.map((data, i) => {
                        const value = data.salesByDepartment[department] || 0;
                        const selected =
                          selectedRow && statementDrill?.report === i;
                        return (
                          <td
                            key={`${i}-${value}`}
                            className={clsx(
                              classes.right,
                              isCompare && selected && classes.highlightedCell,
                              isCompare && classes.pointer
                            )}
                            data-index={i}
                          >
                            {formatDollars(value)}
                          </td>
                        );
                      })}
                    </tr>
                  </Tooltip>
                );
              }
            )}
        </tbody>
        <thead>
          <tr>
            <th className={clsx(classes.bold, classes.left, classes.nowrap)}>
              Cost of Sales
            </th>
            {report.data.map((data, i) => (
              <th className={clsx(classes.bold, classes.right)} key={i}>
                {formatDollars(data.totalCOGS)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {showDetail &&
            getSortedKeys(report.data.map((d) => d.COGSDetail)).map(
              (costType) => {
                const selectedRow =
                  statementDrill?.type === "expense" &&
                  statementDrill.name === costType;

                return (
                  <Tooltip
                    key={costType}
                    title={
                      isCompare
                        ? StatementDrillDownCompareTooltipText
                        : StatementDrillDownTooltipText
                    }
                    placement="top"
                  >
                    <tr
                      className={clsx(
                        !isCompare && classes.pointer,
                        !isCompare && selectedRow && classes.highlightedRow
                      )}
                      onClick={(e) => {
                        recordEvent({
                          type: "feature-adoption",
                          feature: "pl-feature",
                          element: "click-to-view-details",
                        });
                        const report = parseInt(
                          (e.target as HTMLDivElement).getAttribute(
                            "data-index"
                          ) || "",
                          10
                        );

                        onStatementDrillChange(
                          selectedRow &&
                            (!isCompare || report === statementDrill?.report)
                            ? null
                            : {
                                type: "expense",
                                name: costType,
                                ...(isNaN(report) ? {} : { report }),
                              }
                        );
                      }}
                    >
                      <td className={classes.detail}>{costType}</td>
                      {report.data.map((data, i) => {
                        const value = data.COGSDetail[costType] || 0;
                        const selected =
                          selectedRow && statementDrill?.report === i;

                        return (
                          <td
                            key={`${i}-${value}`}
                            className={clsx(
                              classes.right,
                              isCompare && selected && classes.highlightedCell,
                              isCompare && classes.pointer
                            )}
                            data-index={i}
                          >
                            {formatDollars(value)}
                          </td>
                        );
                      })}
                    </tr>
                  </Tooltip>
                );
              }
            )}
          <tr>
            <td className={classes.divider} colSpan={report.data.length + 1} />
          </tr>
        </tbody>
        <thead>
          <tr>
            <th className={clsx(classes.bold, classes.left, classes.nowrap)}>
              Gross Profit
            </th>
            {report.data.map((data, i) => (
              <th className={clsx(classes.bold, classes.right)} key={i}>
                {formatDollars(data.grossProfit)}
              </th>
            ))}
          </tr>
          <tr>
            <th className={clsx(classes.bold, classes.left, classes.nowrap)}>
              Other Business Expenses
            </th>
            {report.data.map((data, i) => (
              <th className={clsx(classes.bold, classes.right)} key={i}>
                {formatDollars(data.totalExpenses)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {showDetail &&
            getSortedKeys(report.data.map((d) => d.expenseDetail)).map(
              (expenseType) => {
                const selectedRow =
                  statementDrill?.type === "transaction" &&
                  statementDrill.name === expenseType;
                return (
                  <Tooltip
                    key={expenseType}
                    title={
                      isCompare
                        ? StatementDrillDownCompareTooltipText
                        : StatementDrillDownTooltipText
                    }
                    placement="top"
                  >
                    <tr
                      className={clsx(
                        !isCompare && classes.pointer,
                        !isCompare && selectedRow && classes.highlightedRow
                      )}
                      onClick={(e) => {
                        recordEvent({
                          type: "feature-adoption",
                          feature: "pl-feature",
                          element: "click-to-view-details",
                        });
                        const report = parseInt(
                          (e.target as HTMLDivElement).getAttribute(
                            "data-index"
                          ) || "",
                          10
                        );

                        onStatementDrillChange(
                          selectedRow &&
                            (!isCompare || report === statementDrill?.report)
                            ? null
                            : {
                                type: "transaction",
                                name: expenseType,
                                ...(isNaN(report) ? {} : { report }),
                              }
                        );
                      }}
                    >
                      <td className={classes.detail}>{expenseType}</td>
                      {report.data.map((data, i) => {
                        const value = data.expenseDetail[expenseType] || 0;
                        const selected =
                          selectedRow && statementDrill?.report === i;
                        return (
                          <td
                            key={`${i}-${value}`}
                            className={clsx(
                              classes.right,
                              isCompare && selected && classes.highlightedCell,
                              isCompare && classes.pointer
                            )}
                            data-index={i}
                          >
                            {formatDollars(value)}
                          </td>
                        );
                      })}
                    </tr>
                  </Tooltip>
                );
              }
            )}
        </tbody>
        <thead>
          {showEstimatedIncomeTax && (
            <tr>
              <th className={clsx(classes.bold, classes.left, classes.nowrap)}>
                Estimated Income Tax
              </th>
              {report.data.map((data, i) => (
                <th className={clsx(classes.bold, classes.right)} key={i}>
                  {formatDollars(data.estimatedIncomeTax)}
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          <tr>
            <td className={classes.divider} colSpan={report.data.length + 1} />
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th className={clsx(classes.left, classes.bold)}>
              {showEstimatedIncomeTax
                ? "Estimated Net Profit"
                : "Operating Profit"}
            </th>
            {report.data.map((data, i) => (
              <th key={i} className={clsx(classes.bold, classes.right)}>
                {formatDollars(
                  data[
                    showEstimatedIncomeTax
                      ? "netProfit"
                      : "netProfitNoIncomeTax"
                  ]
                )}
              </th>
            ))}
          </tr>
        </tfoot>
      </Box>
    );
  }
);

interface ProfitLossReportContentProps {
  report: ProfitLossReportType;
  printable?: boolean;
  profitLossType: ProfitLossType;
  showEstimatedIncomeTax: boolean;
  statementDrill?: StatementDrillTypeWithReport;
  onStatementDrillChange?: (st: StatementDrillTypeWithReport) => void;
  onProfitLossTypeChange?: (t: ProfitLossType) => void;
  onShowEstimatedIncomeTaxChange?: (t: boolean) => void;
  onDownloadClick?: () => void;
  statementDrillRef?: React.Ref<HTMLDivElement>;
}

function ProfitLossReportContent({
  report,
  printable = false,
  profitLossType,
  showEstimatedIncomeTax,
  statementDrill = null,
  onStatementDrillChange = () => {},
  onProfitLossTypeChange,
  onShowEstimatedIncomeTaxChange,
  onDownloadClick,
  statementDrillRef,
}: ProfitLossReportContentProps) {
  const isCompare = report.data.length > 1;
  const titleText = isCompare
    ? ComparisonTitle
    : report.data[0].reportDetails.titleFull;

  return (
    <>
      <CompanyInfo forPrint={printable} />
      {printable ? (
        <Title>{titleText}</Title>
      ) : (
        <ProfitLossReportHeader
          report={report}
          profitLossType={profitLossType}
          onProfitLossTypeChange={onProfitLossTypeChange!}
          showEstimatedIncomeTax={showEstimatedIncomeTax}
          onShowEstimatedIncomeTaxChange={onShowEstimatedIncomeTaxChange!}
          onDownloadClick={onDownloadClick}
        />
      )}
      <ProfitLossReportBody
        report={report}
        showEstimatedIncomeTax={showEstimatedIncomeTax}
        statementDrill={statementDrill}
        onStatementDrillChange={onStatementDrillChange}
        profitLossType={profitLossType}
      />
      {!printable && (
        <div ref={statementDrillRef}>
          <StatementDrill
            data={report.data[statementDrill?.report ?? 0]}
            type={statementDrill}
            onClose={() => onStatementDrillChange(null)}
          />
        </div>
      )}
    </>
  );
}

export interface ProfitLossReportProps {
  report: ProfitLossReportType;
}

function ProfitLossReport({ report }: ProfitLossReportProps) {
  const store = useStore();
  const [profitLossType, setProfitLossType] =
    useState<ProfitLossType>("detail");
  const [showEstimatedIncomeTax, setShowEstimatedIncomeTax] = useState(false);
  const [statementDrill, setStatementDrill] =
    useState<StatementDrillTypeWithReport>(null);
  const statementDrillRef = useRef<HTMLDivElement>(null);
  const isCompare = report.data.length > 1;

  const key = report.data.map((d) => d.reportDetails.titleFull).join("-");
  useEffect(() => {
    setStatementDrill(null);
  }, [key]);

  // Scroll to statement drill when it opens
  useEffect(() => {
    if (statementDrill && statementDrillRef.current) {
      const timeout = setTimeout(() => {
        statementDrillRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [statementDrill]);

  const handleDownloadReport = async () => {
    await printReport(
      <ProfitLossReportContent
        report={report}
        printable
        profitLossType={profitLossType}
        showEstimatedIncomeTax={showEstimatedIncomeTax}
      />,
      { store, title: report.data[0].reportDetails.filename }
    );
  };

  return (
    <Box component={Paper} p={2} mt={2} width="100%">
      <ProfitLossReportContent
        report={report}
        profitLossType={profitLossType}
        showEstimatedIncomeTax={showEstimatedIncomeTax}
        statementDrill={statementDrill}
        onStatementDrillChange={(st) => {
          if (st && isCompare && !("report" in st)) return;
          setStatementDrill(st);
        }}
        onProfitLossTypeChange={(t) => {
          setProfitLossType(t);
          setStatementDrill(null);
        }}
        onShowEstimatedIncomeTaxChange={setShowEstimatedIncomeTax}
        onDownloadClick={() => handleDownloadReport()}
        statementDrillRef={statementDrillRef}
      />
    </Box>
  );
}

export default ProfitLossReport;
